import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import Swal from 'sweetalert2';
import DashboardLayout from '../../components/DashboardLayout';
import axiosInstance from '../../utils/axiosInstance';
import AuthService from '../../services/authService';

const courseTabs = [
  { key: 'HND', label: 'HND - IT' },
  { key: 'BEng-SE', label: 'BEng - SE' },
  { key: 'BEng-NE', label: 'BEng - NE' },
  { key: 'Dip-IT', label: 'Dip - IT' },
  { key: 'Dip-Eng', label: 'Dip - Eng' },
  { key: 'DIM', label: 'DIM' },
  { key: 'KET', label: 'KET' },
  { key: 'HND-BM', label: 'HND - BM' },
  { key: 'BA', label: 'BA' },
  { key: 'Level7', label: 'Level 7' },
];

const statusTabs = [
  { key: 'all', label: 'ALL', color: 'gray' },
  { key: 'ongoing', label: 'Ongoing', color: 'blue' },
  { key: 'not-started', label: 'Not Started', color: 'yellow' },
  { key: 'delivery-completed', label: 'Delivery Completed', color: 'green' },
  { key: 'batch-completed', label: 'Batch Completed', color: 'purple' },
];

const notificationTabs = [
  { key: 'over-scheduled', label: 'OVER SCHEDULED' },
  { key: 'overdue', label: 'Overdue' },
  { key: 'unreleased', label: 'Unreleased' },
];

const notificationExportColumns = [
  { id: 'course', label: 'Course' },
  { id: 'batch', label: 'Batch' },
  { id: 'internal', label: 'Internal' },
  { id: 'whatsapp', label: 'WhatsApp Group' },
  { id: 'subject', label: 'Subject' },
  { id: 'expected', label: 'Expected' },
  { id: 'scheduled', label: 'Scheduled' },
  { id: 'pending', label: 'Pending' },
  { id: 'extraDetail', label: 'Extra Detail' },
  { id: 'lecturer', label: 'Lecturer' },
  { id: 'status', label: 'Status' },
  { id: 'overdueDays', label: 'Overdue Days' },
  { id: 'endDate', label: 'End Date' },
];

const statusColorMap = {
  'not started': 'bg-gray-100 text-gray-800',
  ongoing: 'bg-blue-100 text-blue-800',
  completed: 'bg-green-100 text-green-800',
  'delivery completed': 'bg-purple-100 text-purple-800',
  'batch completed': 'bg-green-100 text-green-800',
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return dateString;
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

const calculateBatchProgress = (startDate, endDate) => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const today = new Date();

  const totalDays = Math.max(0, Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)));
  const passedDays = Math.max(0, Math.ceil((today.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)));
  const pendingDays = Math.max(0, totalDays - passedDays);
  const progress = totalDays > 0 ? Math.min(100, Math.max(0, Math.floor((passedDays / totalDays) * 100))) : 0;

  return { progress, pendingDays, totalDays, passedDays };
};

const calculateAssignmentDates = (submissionDate) => {
  if (!submissionDate) return { releaseDate: '', lateDate: '' };

  const submission = new Date(submissionDate);
  const release = new Date(submission);
  release.setDate(release.getDate() - 30);
  const late = new Date(submission);
  late.setDate(late.getDate() + 7);

  const toValue = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  return { releaseDate: toValue(release), lateDate: toValue(late) };
};

const hasActualAssignmentDates = (subject) => Boolean(
  subject?.actualAssignmentReleaseDate ||
    subject?.actualAssignmentSubmissionDate ||
    subject?.actualAssignmentLateSubmissionDate
);

const toBatchSubjectsRecord = (batch) => {
  const record = {};
  batch.subjectsBySemester.forEach((sem) => {
    const label = `Semester ${sem.semester}`;
    record[label] = sem.subjects;
  });
  return record;
};

const getStatusColor = (status) => statusColorMap[status] || 'bg-gray-100 text-gray-800';

const formatHoursClasses = (hours, classes) => {
  if (hours == null) return 'N/A';
  return `${hours}h${classes != null ? ` (${classes} classes)` : ''}`;
};

const computeOverdueDays = (endDate) => {
  if (!endDate) return 0;
  const end = new Date(endDate);
  if (Number.isNaN(end.getTime())) return 0;
  const today = new Date();
  const endMid = new Date(end);
  const todayMid = new Date(today);
  endMid.setHours(0, 0, 0, 0);
  todayMid.setHours(0, 0, 0, 0);
  const diffDays = Math.floor((todayMid.getTime() - endMid.getTime()) / (1000 * 60 * 60 * 24));
  return diffDays > 0 ? diffDays : 0;
};

const computeDaysDiffFromToday = (dateString) => {
  if (!dateString) return null;
  const target = new Date(dateString);
  if (Number.isNaN(target.getTime())) return null;
  const today = new Date();
  target.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);
  return Math.round((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
};

const daysBetweenDates = (fromDateString, toDateString) => {
  if (!fromDateString || !toDateString) return null;
  const from = new Date(fromDateString);
  const to = new Date(toDateString);
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return null;
  from.setHours(0, 0, 0, 0);
  to.setHours(0, 0, 0, 0);
  return Math.round((to.getTime() - from.getTime()) / (1000 * 60 * 60 * 24));
};

export default function Delivery() {
  const [batches, setBatches] = useState([]);
  const [staff, setStaff] = useState([]);
  const [availableTabs, setAvailableTabs] = useState([]);
  const [activeTab, setActiveTab] = useState('HND');
  const [activeStatusTab, setActiveStatusTab] = useState('ongoing');
  const [searchTerm, setSearchTerm] = useState('');
  const [showSubjectModal, setShowSubjectModal] = useState(false);
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [currentSubject, setCurrentSubject] = useState(null);
  const [currentBatch, setCurrentBatch] = useState(null);
  const [updateType, setUpdateType] = useState('primary');
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [accessibleCourseIds, setAccessibleCourseIds] = useState([]);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [accessLoaded, setAccessLoaded] = useState(false);
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [activeNotificationTab, setActiveNotificationTab] = useState('over-scheduled');
  const [notificationSearchTerm, setNotificationSearchTerm] = useState('');
  const [selectedNotificationIds, setSelectedNotificationIds] = useState([]);
  const [selectedNotificationExportColumns, setSelectedNotificationExportColumns] = useState(
    notificationExportColumns.map((c) => c.id)
  );

  const isAdminUser = useMemo(() => {
    const stored = AuthService.getUser();
    const staff = stored?.superAdmin?.staff;
    const role = String(staff?.role || staff?.position || staff?.designation || staff?.type || '').trim().toLowerCase();
    const superAdmin = Boolean(stored?.superAdmin?.superAdminId);
    const adminRole = role === 'admin' || role === 'administrator';
    return superAdmin || adminRole;
  }, []);

  const accessHeaderValue = useMemo(
    () => (isSuperAdmin ? 'all' : accessibleCourseIds.join(',')),
    [accessibleCourseIds, isSuperAdmin]
  );

  const accessHeaders = useMemo(
    () => (accessHeaderValue ? { 'x-accessible-courses': accessHeaderValue } : {}),
    [accessHeaderValue]
  );

  const fetchAccess = useCallback(async () => {
    try {
      const resp = await axiosInstance.get('/admin/me/coordinate-courses');
      const ids = Array.isArray(resp.data?.accessibleCourseIds) ? resp.data.accessibleCourseIds : [];
      setAccessibleCourseIds(ids);
      setIsSuperAdmin(Boolean(resp.data?.isSuperAdmin));
    } catch (err) {
      console.error('Failed to load accessible courses', err);
      setAccessibleCourseIds([]);
      setIsSuperAdmin(false);
    } finally {
      setAccessLoaded(true);
    }
  }, []);

  const loadDelivery = useCallback(
    async (courseTypeParam, statusParam) => {
      const courseType = courseTypeParam || activeTab;
      const status = statusParam || activeStatusTab;
      setLoading(true);
      setError('');
      try {
        const resp = await axiosInstance.get('/admin/delivery', {
          params: { courseType, status },
          headers: accessHeaders,
        });
        const data = resp.data || {};
        setBatches(data.batches || []);
        setStaff(data.staff || []);
        setAvailableTabs(data.availableTabs || []);
        // Keep the UI tab and status in sync with the user's selection,
        // even if the backend falls back to a different effective courseType.
        setActiveTab(courseType);
        setActiveStatusTab(status);
      } catch (err) {
        console.error('Failed to load delivery data', err);
        setBatches([]);
        setError(err.response?.data?.message || 'Failed to load delivery data');
      } finally {
        setLoading(false);
      }
    },
    [accessHeaders, activeStatusTab, activeTab]
  );

  useEffect(() => {
    fetchAccess();
  }, [fetchAccess]);

  useEffect(() => {
    if (accessLoaded) {
      loadDelivery(activeTab, activeStatusTab);
    }
  }, [accessLoaded, loadDelivery]);

  const handleCourseTabChange = (tabKey) => {
    setActiveTab(tabKey);
    setActiveStatusTab('ongoing');
    loadDelivery(tabKey, 'ongoing');
  };

  const handleStatusTabChange = (statusKey) => {
    setActiveStatusTab(statusKey);
    loadDelivery(activeTab, statusKey);
  };

  const handleSubjectClick = (subject) => {
    setCurrentSubject(subject);
    setUpdateType(subject.status === 'completed' ? 'secondary' : 'primary');
    const currentStaffMember = staff.find((s) => s.name === subject.lecturerName);
    setSelectedStaff(currentStaffMember || null);
    setShowSubjectModal(true);
  };

  const handleBatchClick = (batch) => {
    setCurrentBatch(batch);
    setShowBatchModal(true);
  };

  const handleOpenNotifications = () => {
    if (!isAdminUser) {
      Swal.fire('Restricted', 'Notifications are available for admins only.', 'info');
      return;
    }
    const orderedTabs = ['over-scheduled', 'overdue', 'unreleased'];
    const firstWithData = orderedTabs.find((key) => (notificationBuckets[key] || []).length > 0) || 'over-scheduled';
    setActiveNotificationTab(firstWithData);
    setSelectedNotificationIds([]);
    setShowNotificationModal(true);
  };

  const buildNotificationCopyText = (item) => {
    const overHours =
      item.scheduledHours != null && item.expectedHours != null
        ? item.scheduledHours - item.expectedHours
        : 0;
    const overClasses =
      item.scheduledClasses != null && item.expectedClasses != null
        ? item.scheduledClasses - item.expectedClasses
        : null;
    const overdueDays = computeOverdueDays(item.endDate);

    if (activeNotificationTab === 'unreleased') {
      return [
        `Course: ${item.courseShortName || 'N/A'}`,
        `Batch: ${item.batchName || 'N/A'}`,
        `Subject: ${item.subjectName || 'N/A'}${item.subjectCode ? ` (${item.subjectCode})` : ''}`,
        `Lecturer: ${item.lecturerName || 'N/A'}`,
        `Batch Status: ${item.batchStatus || 'N/A'}`,
        `WhatsApp Group: ${item.whatsappGroupName || 'N/A'}`,
      ].join('\n');
    }

    return [
      `Course: ${item.courseShortName || 'N/A'}`,
      `Batch: ${item.batchName || 'N/A'}`,
      `Subject: ${item.subjectName || 'N/A'}${item.subjectCode ? ` (${item.subjectCode})` : ''}`,
      `Expected: ${formatHoursClasses(item.expectedHours, item.expectedClasses)}`,
      `Scheduled: ${formatHoursClasses(item.scheduledHours, item.scheduledClasses)}`,
      activeNotificationTab === 'over-scheduled' && overHours > 0
        ? `Over Scheduled: +${overHours.toFixed(1)}h${
            overClasses != null ? ` (${overClasses.toFixed(1)} classes)` : ''
          }`
        : null,
      activeNotificationTab === 'overdue' && overdueDays > 0
        ? `Overdue by: ${overdueDays} day${overdueDays === 1 ? '' : 's'}`
        : null,
      `Pending: ${formatHoursClasses(item.pendingHours, item.pendingClasses)}`,
      `Lecturer: ${item.lecturerName || 'N/A'}`,
      `Batch Status: ${item.batchStatus || 'N/A'}`,
      `WhatsApp Group: ${item.whatsappGroupName || 'N/A'}`,
    ].filter(Boolean).join('\n');
  };

  const buildNotificationKey = (item) =>
    `${activeNotificationTab}-${item.batchSubjectId}-${item.batchId}-${item.subjectCode || item.subjectName || 'subject'}`;

  const handleCopyNotification = async (item) => {
    const text = buildNotificationCopyText(item);
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      Swal.fire('Copied', 'Details copied to clipboard.', 'success');
    } catch (err) {
      console.error('Failed to copy notification text', err);
      Swal.fire('Error', 'Could not copy to clipboard.', 'error');
    }
  };

  const handleCopySelectedNotifications = async () => {
    const selectedItems = filteredNotificationList.filter((item) => selectedNotificationIds.includes(buildNotificationKey(item)));
    if (selectedItems.length === 0) {
      Swal.fire('Nothing selected', 'Please select at least one row to copy.', 'info');
      return;
    }
    const text = selectedItems.map((item) => buildNotificationCopyText(item)).join('\n\n');
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      Swal.fire('Copied', 'Selected details copied to clipboard.', 'success');
    } catch (err) {
      console.error('Failed to copy selected notification text', err);
      Swal.fire('Error', 'Could not copy to clipboard.', 'error');
    }
  };

  const toggleNotificationExportColumn = (id) => {
    setSelectedNotificationExportColumns((prev) =>
      prev.includes(id) ? prev.filter((colId) => colId !== id) : [...prev, id]
    );
  };

  const handleDownloadNotificationsExcel = async () => {
    const selectedItems = filteredNotificationList.filter((item) =>
      selectedNotificationIds.includes(buildNotificationKey(item))
    );

    if (selectedItems.length === 0) {
      Swal.fire('Nothing selected', 'Please select at least one row to download.', 'info');
      return;
    }

    if (!selectedNotificationExportColumns.length) {
      Swal.fire('No columns selected', 'Please select at least one column to include in the export.', 'info');
      return;
    }

    try {
      const XLSX = await import('xlsx');

      const include = (id) => selectedNotificationExportColumns.includes(id);
      const headers = ['Notification Type'];
      if (include('course')) headers.push('Course');
      if (include('batch')) headers.push('Batch');
      if (include('internal')) headers.push('Batch Internal');
      if (include('whatsapp')) headers.push('WhatsApp Group');
      if (include('subject')) headers.push('Subject');
      if (include('expected')) headers.push('Expected Hours');
      if (include('scheduled')) headers.push('Scheduled Hours');
      if (include('pending')) headers.push('Pending Hours');
      if (include('extraDetail')) headers.push('Over Scheduled Detail');
      if (include('lecturer')) headers.push('Lecturer');
      if (include('status')) headers.push('Status');
      if (include('overdueDays')) headers.push('Overdue Days');
      if (include('endDate')) headers.push('End Date');

      const rows = selectedItems.map((item) => {
        const overdueDays = computeOverdueDays(item.endDate);
        const overHours =
          item.scheduledHours != null && item.expectedHours != null
            ? item.scheduledHours - item.expectedHours
            : 0;
        const overClasses =
          item.scheduledClasses != null && item.expectedClasses != null
            ? item.scheduledClasses - item.expectedClasses
            : null;
        let overDetail = '';
        if (activeNotificationTab === 'overdue' && overdueDays > 0) {
          overDetail = `Overdue\r\nby ${overdueDays} day${overdueDays === 1 ? '' : 's'}`;
        } else if (activeNotificationTab === 'over-scheduled' && overHours > 0) {
          const hoursText = `+${overHours.toFixed(1)}h`;
          const classesText =
            overClasses != null ? `(${overClasses.toFixed(1)} classes)` : '';
          overDetail = classesText ? `${hoursText}\r\n${classesText}` : hoursText;
        }

        const row = [
          notificationTabs.find((t) => t.key === activeNotificationTab)?.label || activeNotificationTab,
        ];

        if (include('course')) row.push(item.courseShortName || 'N/A');
        if (include('batch')) row.push(item.batchName || 'N/A');
        if (include('internal')) row.push(item.batchInternalName || 'N/A');
        if (include('whatsapp')) row.push(item.whatsappGroupName || 'N/A');
        if (include('subject')) row.push(item.subjectName || 'N/A');
        if (include('expected')) row.push(item.expectedHours != null ? item.expectedHours : '');
        if (include('scheduled')) row.push(item.scheduledHours != null ? item.scheduledHours : '');
        if (include('pending')) row.push(item.pendingHours != null ? item.pendingHours : '');
        if (include('extraDetail')) row.push(overDetail);
        if (include('lecturer')) row.push(item.lecturerName || 'N/A');
        if (include('status')) row.push(item.status || '');
        if (include('overdueDays')) row.push(overdueDays > 0 ? overdueDays : '');
        if (include('endDate')) row.push(item.endDate ? formatDate(item.endDate) : '');

        return row;
      });

      const worksheetData = [headers, ...rows];
      const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Delivery Notifications');

      const safeCourse = activeCourseLabel.replace(/\s+/g, '_');
      const safeType = (notificationTabs.find((t) => t.key === activeNotificationTab)?.label || activeNotificationTab)
        .replace(/\s+/g, '_');
      const today = new Date().toISOString().split('T')[0];
      const fileName = `Delivery_Notifications_${safeCourse}_${safeType}_${today}.xlsx`;

      XLSX.writeFile(workbook, fileName);
    } catch (err) {
      console.error('Failed to download notifications Excel', err);
      Swal.fire('Download failed', 'Unable to create the Excel file. Please try again.', 'error');
    }
  };

  const handleDownloadNotificationsPdf = () => {
    const selectedItems = filteredNotificationList.filter((item) =>
      selectedNotificationIds.includes(buildNotificationKey(item))
    );

    if (selectedItems.length === 0) {
      Swal.fire('Nothing selected', 'Please select at least one row to download.', 'info');
      return;
    }

    const notificationLabel =
      notificationTabs.find((t) => t.key === activeNotificationTab)?.label || activeNotificationTab;
    const now = new Date();
    const generatedAt = now.toLocaleString();

    const escapeHtml = (value) =>
      String(value || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');

    if (!selectedNotificationExportColumns.length) {
      Swal.fire('No columns selected', 'Please select at least one column to include in the export.', 'info');
      return;
    }

    const include = (id) => selectedNotificationExportColumns.includes(id);

    const groupedByWhatsapp = selectedItems.reduce((acc, item) => {
      const key = item.whatsappGroupName && item.whatsappGroupName.trim().length > 0
        ? item.whatsappGroupName
        : 'No WhatsApp Group';
      if (!acc[key]) acc[key] = [];
      acc[key].push(item);
      return acc;
    }, {});

    const uniqueWhatsappGroups = Object.keys(groupedByWhatsapp).filter((name) => name && name.trim().length > 0);

    const buildRowHtml = (item, index) => {
        const overdueDays = computeOverdueDays(item.endDate);
        const overHours =
          item.scheduledHours != null && item.expectedHours != null
            ? item.scheduledHours - item.expectedHours
            : 0;
        const overClasses =
          item.scheduledClasses != null && item.expectedClasses != null
            ? item.scheduledClasses - item.expectedClasses
            : null;

        let overDetailHtml = '';
        if (activeNotificationTab === 'overdue' && overdueDays > 0) {
          overDetailHtml = `${escapeHtml('Overdue')}<br />${escapeHtml(
            `by ${overdueDays} day${overdueDays === 1 ? '' : 's'}`
          )}`;
        } else if (activeNotificationTab === 'over-scheduled' && overHours > 0) {
          const hoursText = `+${overHours.toFixed(1)}h`;
          const classesText =
            overClasses != null ? `(${overClasses.toFixed(1)} classes)` : '';
          overDetailHtml = classesText
            ? `${escapeHtml(hoursText)}<br />${escapeHtml(classesText)}`
            : escapeHtml(hoursText);
        }

        const cells = [];
        if (include('course')) cells.push(`<td>${escapeHtml(item.courseShortName || 'N/A')}</td>`);
        if (include('batch')) cells.push(`<td>${escapeHtml(item.batchName || 'N/A')}</td>`);
        if (include('internal')) cells.push(`<td>${escapeHtml(item.batchInternalName || 'N/A')}</td>`);
        if (include('whatsapp')) cells.push(`<td>${escapeHtml(item.whatsappGroupName || 'N/A')}</td>`);
        if (include('subject')) {
          cells.push(
            `<td>${escapeHtml(item.subjectName || 'N/A')}<br /><span style="font-size:10px;color:#6b7280;">${escapeHtml(
              item.subjectCode || ''
            )}</span></td>`
          );
        }
        if (include('expected')) {
          cells.push(
            `<td>${escapeHtml(
              item.expectedHours != null
                ? `${item.expectedHours}h (${item.expectedClasses ?? '-'} classes)`
                : 'N/A'
            )}</td>`
          );
        }
        if (include('scheduled')) {
          cells.push(
            `<td>${escapeHtml(
              item.scheduledHours != null
                ? `${item.scheduledHours}h (${item.scheduledClasses ?? '-'} classes)`
                : 'N/A'
            )}</td>`
          );
        }
        if (include('pending')) {
          cells.push(
            `<td>${escapeHtml(
              item.pendingHours != null
                ? `${item.pendingHours}h (${item.pendingClasses ?? '-'} classes)`
                : 'N/A'
            )}</td>`
          );
        }
        if (include('extraDetail')) cells.push(`<td>${overDetailHtml}</td>`);
        if (include('lecturer')) cells.push(`<td>${escapeHtml(item.lecturerName || 'N/A')}</td>`);
        if (include('status')) cells.push(`<td>${escapeHtml(item.status || '')}</td>`);
        if (include('overdueDays')) {
          cells.push(
            `<td>${escapeHtml(
              overdueDays > 0 ? `${overdueDays} day${overdueDays === 1 ? '' : 's'}` : ''
            )}</td>`
          );
        }
        if (include('endDate')) {
          cells.push(`<td>${escapeHtml(item.endDate ? formatDate(item.endDate) : '')}</td>`);
        }

        return `
          <tr>
            <td>${index + 1}</td>
            ${cells.join('')}
          </tr>
        `;
      };

    const tableHeaderHtml = `
      <thead>
        <tr>
          <th>#</th>
          ${include('course') ? '<th>Course</th>' : ''}
          ${include('batch') ? '<th>Batch</th>' : ''}
          ${include('internal') ? '<th>Internal (Report No)</th>' : ''}
          ${include('whatsapp') ? '<th>WhatsApp Group</th>' : ''}
          ${include('subject') ? '<th>Subject</th>' : ''}
          ${include('expected') ? '<th>Expected</th>' : ''}
          ${include('scheduled') ? '<th>Scheduled</th>' : ''}
          ${include('pending') ? '<th>Pending</th>' : ''}
          ${include('extraDetail') ? '<th>Over Scheduled Detail</th>' : ''}
          ${include('lecturer') ? '<th>Lecturer</th>' : ''}
          ${include('status') ? '<th>Status</th>' : ''}
          ${include('overdueDays') ? '<th>Overdue Days</th>' : ''}
          ${include('endDate') ? '<th>End Date</th>' : ''}
        </tr>
      </thead>`;

    const groupedTablesHtml = Object.entries(groupedByWhatsapp)
      .map(([groupName, items]) => {
        const rowsHtml = items
          .map((item, index) => buildRowHtml(item, index))
          .join('');

        return `
          <h2 style="font-size:14px;margin:16px 0 4px 0;">WhatsApp Group: ${escapeHtml(groupName)}</h2>
          <table>
            ${tableHeaderHtml}
            <tbody>
              ${rowsHtml}
            </tbody>
          </table>
        `;
      })
      .join('<br />');

    const html = `
      <html>
        <head>
          <meta charset="utf-8" />
          <title>Delivery Notifications - ${escapeHtml(notificationLabel)}</title>
          <style>
            body { font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; padding: 24px; color: #111827; }
            h1 { font-size: 24px; margin-bottom: 4px; }
            .meta { font-size: 12px; color: #6b7280; margin-bottom: 16px; }
            .meta span { display: inline-block; margin-right: 16px; }
            table { width: 100%; border-collapse: collapse; font-size: 11px; }
            thead tr { background: #f3f4f6; }
            th, td { border: 1px solid #e5e7eb; padding: 6px 8px; text-align: left; }
            th { font-weight: 600; }
            tbody tr:nth-child(even) { background: #f9fafb; }
          </style>
        </head>
        <body>
          <h1>Delivery Notifications - ${escapeHtml(notificationLabel)}</h1>
          <div class="meta">
            <span><strong>Course:</strong> ${escapeHtml(activeCourseLabel)}</span>
            <span><strong>WhatsApp Group:</strong> ${
              uniqueWhatsappGroups.length === 1
                ? escapeHtml(uniqueWhatsappGroups[0])
                : escapeHtml('Multiple / Not Specified')
            }</span>
            <span><strong>Notification Type:</strong> ${escapeHtml(notificationLabel)}</span>
            <span><strong>Generated At:</strong> ${escapeHtml(generatedAt)}</span>
            <span><strong>Total Rows:</strong> ${selectedItems.length}</span>
          </div>
          ${groupedTablesHtml}
        </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      Swal.fire('Popup blocked', 'Allow popups for this site to download the PDF.', 'warning');
      return;
    }

    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();

    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 300);
  };

  const handleToggleNotificationSelect = (item) => {
    const key = buildNotificationKey(item);
    setSelectedNotificationIds((prev) =>
      prev.includes(key) ? prev.filter((id) => id !== key) : [...prev, key]
    );
  };

  const handlePrimaryUpdate = async () => {
    if (!currentSubject) return;

    const result = await Swal.fire({
      title: 'Update batch subject? ',
      text: 'This will update matching batch groups.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, update',
      confirmButtonColor: '#4f46e5',
    });

    if (!result.isConfirmed) return;

    try {
      await axiosInstance.put(
        `/admin/batch-groups/batch-subjects/${currentSubject.batchSubjectId}`,
        {
          status: currentSubject.status,
          staffId: selectedStaff ? selectedStaff.value : null,
        },
        { headers: accessHeaders }
      );
      Swal.fire('Updated', 'Batch subject updated.', 'success');
      setShowSubjectModal(false);
      setSelectedStaff(null);
      loadDelivery(activeTab, activeStatusTab);
    } catch (err) {
      console.error('Failed to update batch subject', err);
      Swal.fire('Error', err.response?.data?.message || 'Failed to update batch subject.', 'error');
    }
  };

  const handleSecondaryUpdate = async () => {
    if (!currentSubject) return;

    const result = await Swal.fire({
      title: 'Update assignment dates?',
      text: 'Actual assignment dates will be saved.',
      icon: 'info',
      showCancelButton: true,
      confirmButtonText: 'Yes, save',
      confirmButtonColor: '#4f46e5',
    });

    if (!result.isConfirmed) return;

    try {
      await axiosInstance.put(
        `/admin/batch-groups/batch-subjects/${currentSubject.batchSubjectId}`,
        {
          actualAssignmentReleaseDate: currentSubject.actualAssignmentReleaseDate || null,
          actualAssignmentSubmissionDate: currentSubject.actualAssignmentSubmissionDate || null,
          actualAssignmentLateSubmissionDate: currentSubject.actualAssignmentLateSubmissionDate || null,
        },
        { headers: accessHeaders }
      );
      Swal.fire('Updated', 'Assignment dates saved.', 'success');
      setShowSubjectModal(false);
      loadDelivery(activeTab, activeStatusTab);
    } catch (err) {
      console.error('Failed to update assignment dates', err);
      Swal.fire('Error', err.response?.data?.message || 'Failed to update assignment dates.', 'error');
    }
  };

  const handleClearActualDates = async () => {
    if (!currentSubject) return;

    const result = await Swal.fire({
      title: 'Clear actual assignment dates?',
      text: 'All actual assignment dates will be removed.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, clear',
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
    });

    if (!result.isConfirmed) return;

    try {
      await axiosInstance.put(
        `/admin/batch-groups/batch-subjects/${currentSubject.batchSubjectId}`,
        {
          actualAssignmentReleaseDate: null,
          actualAssignmentSubmissionDate: null,
          actualAssignmentLateSubmissionDate: null,
        },
        { headers: accessHeaders }
      );
      Swal.fire('Cleared', 'Actual assignment dates removed.', 'success');
      setShowSubjectModal(false);
      loadDelivery(activeTab, activeStatusTab);
    } catch (err) {
      console.error('Failed to clear assignment dates', err);
      Swal.fire('Error', err.response?.data?.message || 'Failed to clear assignment dates.', 'error');
    }
  };

  const handleBatchStatusUpdate = async () => {
    if (!currentBatch) return;

    const result = await Swal.fire({
      title: 'Update batch status?',
      text: 'Batch status will be changed.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, update',
      confirmButtonColor: '#4f46e5',
    });

    if (!result.isConfirmed) return;

    try {
      await axiosInstance.patch(
        `/admin/batches/${currentBatch.batchId}/status`,
        { status: currentBatch.status },
        { headers: accessHeaders }
      );
      Swal.fire('Updated', 'Batch status updated.', 'success');
      setShowBatchModal(false);
      loadDelivery(activeTab, activeStatusTab);
    } catch (err) {
      console.error('Failed to update batch status', err);
      Swal.fire('Error', err.response?.data?.message || 'Failed to update batch status.', 'error');
    }
  };

  const filteredBatches = useMemo(() => {
    if (!searchTerm.trim()) return batches;
    const lowerSearch = searchTerm.toLowerCase();
    return batches.filter((batch) => {
      const batchNameMatch = batch.batchName?.toLowerCase().includes(lowerSearch);
      const whatsappGroupMatch = batch.whatsappGroupName?.toLowerCase().includes(lowerSearch);
      const internalNameMatch = batch.batchInternalName?.toLowerCase().includes(lowerSearch);
      return batchNameMatch || whatsappGroupMatch || internalNameMatch;
    });
  }, [batches, searchTerm]);

  const groupedByWhatsapp = useMemo(() => {
    const groups = {};
    filteredBatches.forEach((b) => {
      const key = b.whatsappGroupName || 'No Group';
      if (!groups[key]) groups[key] = [];
      groups[key].push({ ...b, batchSubjects: toBatchSubjectsRecord(b) });
    });
    Object.keys(groups).forEach((k) => {
      groups[k].sort((a, b) => {
        const aIsSoftware = a.courseShortName?.toLowerCase().includes('software') || false;
        const bIsSoftware = b.courseShortName?.toLowerCase().includes('software') || false;
        if (aIsSoftware && !bIsSoftware) return -1;
        if (!aIsSoftware && bIsSoftware) return 1;
        return 0;
      });
    });
    return groups;
  }, [filteredBatches]);

  const notificationBuckets = useMemo(() => {
    const buckets = {
      'over-scheduled': [],
      overdue: [],
      unreleased: [],
    };

    filteredBatches.forEach((batch) => {
      const batchInfo = {
        batchId: batch.batchId,
        courseShortName: batch.courseShortName,
        batchName: batch.batchName,
        batchInternalName: batch.batchInternalName,
        whatsappGroupName: batch.whatsappGroupName,
        batchStatus: batch.status,
      };

      const subjectsBySemester = batch.batchSubjects || toBatchSubjectsRecord(batch);

      Object.values(subjectsBySemester || {}).forEach((subs) => {
        subs.forEach((subject) => {
          const isCompleted = subject.status === 'completed';
          const endPassed = subject.endDate && new Date(subject.endDate) < new Date();
          const expectedHours = typeof subject.numberOfClasses === 'number' ? subject.numberOfClasses * 2 : null;
          const expectedClasses = typeof subject.numberOfClasses === 'number' ? subject.numberOfClasses : null;
          const scheduledHours = typeof subject.scheduledHours === 'number' ? subject.scheduledHours : null;
          const scheduledClasses =
            subject.scheduledClasses ?? (scheduledHours != null ? Math.round(scheduledHours / 2) : null);
          const pendingHours = typeof subject.pendingHours === 'number' ? subject.pendingHours : null;
          const pendingClasses = subject.pendingClasses ?? (pendingHours != null ? Math.round(pendingHours / 2) : null);

          const overScheduled =
            scheduledHours != null && expectedHours != null && scheduledHours > expectedHours && !isCompleted;
          const overdue = endPassed && !isCompleted;
          const unreleased = isCompleted && !subject.actualAssignmentSubmissionDate;

          const baseSubject = {
            ...batchInfo,
            batchSubjectId: subject.batchSubjectId,
            subjectName: subject.subjectName,
            subjectCode: subject.subjectCode,
            lecturerName: subject.lecturerName || 'N/A',
            expectedHours,
            expectedClasses,
            scheduledHours,
            scheduledClasses,
            pendingHours,
            pendingClasses,
            status: subject.status,
            endDate: subject.endDate || null,
          };

          if (overScheduled) buckets['over-scheduled'].push(baseSubject);
          if (overdue) buckets.overdue.push(baseSubject);
          if (unreleased) buckets.unreleased.push(baseSubject);
        });
      });
    });

    return buckets;
  }, [filteredBatches]);

  const visibleTabs = useMemo(() => {
    // Admins always see all course type tabs.
    if (isAdminUser) {
      return courseTabs;
    }

    // Until access is resolved, keep default tabs to avoid flicker.
    if (!accessLoaded) {
      return courseTabs;
    }

    // For non-admin users, only show tabs that:
    // - have at least one accessible course for the user, and
    // - have at least one batch for the current status filter.
    // The backend pre-computes these in `availableTabs`.
    if (!Array.isArray(availableTabs) || availableTabs.length === 0) {
      return [];
    }

    return courseTabs.filter((tab) => availableTabs.includes(tab.key));
  }, [isAdminUser, accessLoaded, availableTabs]);
  const currentNotificationList = notificationBuckets[activeNotificationTab] || [];
  const activeCourseLabel = useMemo(
    () => courseTabs.find((course) => course.key === activeTab)?.label || activeTab,
    [activeTab]
  );
  const filteredNotificationList = useMemo(() => {
    const tokens = notificationSearchTerm
      .split(',')
      .map((t) => t.trim().toLowerCase())
      .filter(Boolean);

    if (tokens.length === 0) return currentNotificationList;

    return currentNotificationList.filter((item) => {
      const fields = [
        item.courseShortName,
        item.batchName,
        item.batchInternalName,
        item.whatsappGroupName,
        item.subjectName,
        item.subjectCode,
        item.lecturerName,
        item.status,
      ];
      return tokens.some((token) => fields.some((field) => field && field.toLowerCase().includes(token)));
    });
  }, [currentNotificationList, notificationSearchTerm]);

  const groupedNotifications = useMemo(() => {
    const grouped = {};
    filteredNotificationList.forEach((item) => {
      const key = `${item.whatsappGroupName || 'No Group'}||${item.subjectName || 'N/A'}`;
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(item);
    });
    return grouped;
  }, [filteredNotificationList]);

  useEffect(() => {
    setSelectedNotificationIds([]);
  }, [activeNotificationTab]);

  useEffect(() => {
    setSelectedNotificationIds((prev) =>
      prev.filter((id) => filteredNotificationList.some((item) => buildNotificationKey(item) === id))
    );
  }, [filteredNotificationList]);

  useEffect(() => {
    // For non-admin users, if the currently active course tab is not part of the
    // visible set (no coordinated access or no batches for the current status),
    // automatically switch to the first visible tab.
    if (isAdminUser) return;
    if (!accessLoaded) return;
    if (!visibleTabs || visibleTabs.length === 0) return;

    const isActiveVisible = visibleTabs.some((tab) => tab.key === activeTab);
    if (!isActiveVisible) {
      const nextTabKey = visibleTabs[0].key;
      setActiveTab(nextTabKey);
      setActiveStatusTab('ongoing');
      loadDelivery(nextTabKey, 'ongoing');
    }
  }, [isAdminUser, accessLoaded, visibleTabs, activeTab, loadDelivery]);

  const renderStatusContent = () => {
    if (filteredBatches.length === 0) {
      return (
        <div className="rounded-2xl border-2 border-dashed border-gray-300 bg-gray-50 p-12 text-center">
          <svg className="mx-auto h-16 w-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
          </svg>
          <h3 className="mt-4 text-lg font-semibold text-gray-900">No batches found</h3>
          <p className="mt-2 text-sm text-gray-500">
            {searchTerm
              ? `No batches match your search "${searchTerm}"`
              : 'There are no batches for the selected course and status filter.'}
          </p>
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="mt-4 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700"
            >
              Clear Search
            </button>
          )}
        </div>
      );
    }

    return (
      <div className="space-y-10">
        {Object.entries(groupedByWhatsapp).map(([groupName, groupBatches]) => {
          const filteredGroup = groupBatches;
          if (filteredGroup.length === 0) return null;

          const baseline = filteredGroup[0];
          const semesters = Object.entries(baseline.batchSubjects);
          const totalSubjectColumns = semesters.reduce((acc, [, subs]) => acc + subs.length, 0);

          return (
            <div key={groupName} className="w-full overflow-x-auto mb-8">
              <h2 className="mb-4 text-2xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">{groupName}</h2>
              <table className="min-w-[900px] border-collapse w-full shadow-xl rounded-lg overflow-hidden">
                <thead>
                  <tr className="bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-700 text-white">
                    <th className="border-2 border-indigo-800 p-4 text-center align-middle font-bold text-sm uppercase tracking-wider shadow-inner" rowSpan={2}>BATCH NAME</th>
                    {semesters.map(([semLabel, subs]) => (
                      <th key={semLabel} className="border-2 border-indigo-800 p-4 text-center font-bold text-sm uppercase tracking-wider shadow-inner" colSpan={subs.length}>{semLabel.toUpperCase()}</th>
                    ))}
                  </tr>
                  <tr className="bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-700 text-white">
                    {semesters.map(([, subs]) =>
                      subs.map((s) => (
                        <th key={s.batchSubjectId} className="border-2 border-indigo-800 p-3 text-center font-semibold text-xs uppercase tracking-wide shadow-inner">{s.subjectCode}</th>
                      ))
                    )}
                  </tr>
                </thead>
                <tbody>
                  {filteredGroup.map((batch) => {
                    const isNetwork = batch.courseShortName?.toLowerCase().includes('network');
                    const isSoftware = batch.courseShortName?.toLowerCase().includes('software');
                    const bannerBg = isNetwork
                      ? 'bg-gradient-to-r from-teal-700 via-emerald-700 to-teal-800'
                      : isSoftware
                      ? 'bg-gradient-to-r from-slate-700 via-blue-800 to-slate-800'
                      : 'bg-gradient-to-r from-gray-400 via-gray-500 to-gray-400';
                    const bannerText = isNetwork || isSoftware ? 'text-white' : 'text-gray-900';

                    const { progress, pendingDays, totalDays, passedDays } = calculateBatchProgress(
                      batch.batchStartingDate,
                      batch.batchEndingDate
                    );

                    return (
                      <React.Fragment key={batch.batchId}>
                        <tr>
                          <td className={`${bannerBg} ${bannerText} border-2 border-gray-600 p-3 text-left uppercase shadow-lg`} colSpan={1 + totalSubjectColumns}>
                            <div className="font-bold text-sm tracking-wide drop-shadow-md">{batch.courseShortName || 'N/A'}</div>
                          </td>
                        </tr>

                        <tr className="hover:bg-indigo-50 transition-colors duration-200">
                          <td
                            className="align-top border-2 border-gray-300 bg-gradient-to-br from-blue-50 to-indigo-50 p-3 shadow-sm cursor-pointer hover:shadow-md transition-all"
                            rowSpan={2}
                            onDoubleClick={() => handleBatchClick(batch)}
                            title="Double-click to edit batch status"
                          >
                            <div className="text-sm font-bold text-gray-900">{batch.batchName}</div>
                            <div className="mt-2 inline-block rounded-full px-3 py-1 text-xs font-bold shadow-md bg-gradient-to-r from-blue-500 to-indigo-600 text-white">
                              {batch.status}
                            </div>
                            <hr className="my-2 border-indigo-200" />
                            {batch.batchInternalName && (
                              <div className="text-xs text-gray-700"><span className="text-indigo-700 font-bold">IN :</span> {batch.batchInternalName}</div>
                            )}
                            <hr className="my-2 border-indigo-200" />
                            {batch.whatsappGroupName && (
                              <div className="text-xs text-gray-700"><span className="text-green-700 font-bold">WG :</span> {batch.whatsappGroupName}</div>
                            )}
                          </td>

                          {Object.entries(batch.batchSubjects).map(([semLabel, subs]) =>
                            subs.map((subject) => {
                              const endPassed = new Date(subject.endDate) < new Date();
                              const isCompleted = subject.status === 'completed';
                              const actualDates = hasActualAssignmentDates(subject);
                              const expectedHours = typeof subject.numberOfClasses === 'number' ? subject.numberOfClasses * 2 : null;
                              const overScheduled =
                                typeof subject.scheduledHours === 'number' &&
                                typeof expectedHours === 'number' &&
                                subject.scheduledHours > expectedHours;
                              const missingSubmissionDate = isCompleted && !subject.actualAssignmentSubmissionDate;

                              let bg = 'bg-white';
                              let borderColor = 'border-gray-300';
                              let borderWidth = 'border-2';

                              if (missingSubmissionDate) {
                                bg = 'bg-gradient-to-br from-amber-100 via-yellow-100 to-amber-50';
                                borderColor = 'border-amber-400';
                                borderWidth = 'border-4';
                              } else if (actualDates) {
                                bg = 'bg-gradient-to-br from-green-200 via-emerald-200 to-green-100';
                                borderColor = 'border-green-500';
                                borderWidth = 'border-4';
                              } else if (overScheduled && !isCompleted) {
                                bg = 'bg-gradient-to-br from-fuchsia-100 via-pink-100 to-rose-100';
                                borderColor = 'border-fuchsia-400';
                                borderWidth = 'border-4';
                              } else if (endPassed && !isCompleted) {
                                bg = 'bg-gradient-to-br from-red-50 to-red-100';
                                borderColor = 'border-red-300';
                                borderWidth = 'border-4';
                              } else if (endPassed && isCompleted) {
                                bg = 'bg-gradient-to-br from-gray-50 to-gray-100';
                                borderColor = 'border-gray-400';
                              } else if (isCompleted) {
                                bg = 'bg-gradient-to-br from-green-50 to-emerald-100';
                                borderColor = 'border-green-300';
                              }

                              return (
                                <td
                                  key={subject.batchSubjectId}
                                  className={`align-top ${borderWidth} ${borderColor} p-3 ${bg} hover:shadow-lg transition-all duration-200 cursor-pointer ${
                                    actualDates && !missingSubmissionDate ? 'shadow-md ring-2 ring-green-400' : ''
                                  } ${overScheduled && !isCompleted && !actualDates && !missingSubmissionDate ? 'shadow-md ring-2 ring-fuchsia-400' : ''} ${
                                    missingSubmissionDate ? 'shadow-md ring-2 ring-amber-400' : ''
                                  }`}
                                  onDoubleClick={() => handleSubjectClick(subject)}
                                  title={
                                    missingSubmissionDate
                                      ? 'Completed but actual submission date is missing. Double-click to edit.'
                                      : actualDates
                                      ? 'Actual assignment dates recorded. Double-click to edit.'
                                      : 'Double-click to edit subject.'
                                  }
                                >
                                  <span className="inline-block rounded-md bg-gradient-to-r from-gray-800 to-black px-3 py-1 text-xs font-bold text-white shadow-md">{subject.subjectCode}</span>
                                  <hr className="my-2 border-gray-300" />
                                  {subject.numberOfClasses != null && (
                                    <span className="ml-2 inline-flex items-center rounded-full bg-cyan-100 text-cyan-800 px-2 py-0.5 text-[10px] font-bold whitespace-nowrap">
                                      {`E : ${subject.numberOfClasses * 2}h ( ${subject.numberOfClasses} )`}
                                    </span>
                                  )}
                                  <hr className="my-2 border-gray-300" />
                                  {typeof subject.scheduledHours === 'number' && (
                                    <span
                                      className={`ml-2 inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold whitespace-nowrap ${
                                        overScheduled && !isCompleted ? 'bg-rose-100 text-rose-800' : 'bg-violet-100 text-violet-800'
                                      }`}
                                      title="Scheduled hours and classes"
                                    >
                                      {`S : ${subject.scheduledHours}h ( ${subject.scheduledClasses ?? Math.round((subject.scheduledHours || 0) / 2)} )`}
                                    </span>
                                  )}
                                  <hr className="my-2 border-gray-300" />
                                  {typeof subject.pendingHours === 'number' && (
                                    <span
                                      className="ml-2 inline-flex items-center rounded-full bg-amber-100 text-amber-800 px-2 py-0.5 text-[10px] font-bold whitespace-nowrap"
                                      title="Pending hours and classes to schedule"
                                    >
                                      {`P : ${subject.pendingHours}h ( ${subject.pendingClasses ?? Math.round((subject.pendingHours || 0) / 2)} )`}
                                    </span>
                                  )}
                                  {overScheduled && !isCompleted && (
                                    <span className="ml-2 inline-block rounded-full bg-fuchsia-600 px-2 py-0.5 text-[10px] font-bold text-white shadow-sm" title="Scheduled hours exceed expected hours">
                                      OVER SCHEDULED
                                    </span>
                                  )}
                                  {missingSubmissionDate && (
                                    <span className="ml-2 inline-block rounded-full bg-amber-500 px-2 py-0.5 text-[10px] font-bold text-white shadow-sm" title="Subject is completed but actual submission date is missing">
                                      Unreleased
                                    </span>
                                  )}
                                  {endPassed && !isCompleted && (
                                    <span className="ml-2 inline-block rounded-full bg-red-600 px-2 py-0.5 text-[10px] font-bold text-white shadow-sm" title="End date has passed for this subject">
                                      Overdue
                                    </span>
                                  )}
                                  {actualDates && (
                                    <span className="ml-2 inline-block rounded-full bg-green-600 px-2 py-0.5 text-[10px] font-bold text-white shadow-sm">
                                      Released
                                    </span>
                                  )}
                                  <hr className="my-2 border-gray-300" />
                                  <span className={`inline-block rounded-full px-3 py-1 text-xs font-bold shadow-sm ${getStatusColor(subject.status)}`}>
                                    {subject.status}
                                  </span>
                                  <hr className="my-2 border-gray-300" />
                                  {subject.lecturerName && (
                                    <div className="text-xs font-semibold text-gray-800">{subject.lecturerName}</div>
                                  )}
                                  {subject.lecturerName && <hr className="my-2 border-gray-300" />}
                                  <div className="text-[11px] text-gray-600">
                                    <span className="text-gray-800 font-bold">From:</span> {formatDate(subject.startDate)} <br />
                                    <span className="text-gray-800 font-bold">To:</span> {formatDate(subject.endDate)}
                                  </div>
                                </td>
                              );
                            })
                          )}
                        </tr>

                        <tr className="bg-gradient-to-r from-slate-50 to-gray-50">
                          <td className="border-2 border-gray-300 p-4 shadow-inner" colSpan={totalSubjectColumns}>
                            <div className="flex justify-between text-xs font-bold mb-2 text-gray-700">
                              <span className="text-indigo-700">Progress: {progress}%</span>
                              <span className="text-amber-700">Pending: {pendingDays} days</span>
                            </div>
                            <div className="h-4 w-full rounded-full bg-gray-200 overflow-hidden shadow-inner border border-gray-300">
                              <div
                                className={`h-full rounded-full transition-all duration-500 ${
                                  progress < 33
                                    ? 'bg-gradient-to-r from-green-400 to-emerald-600'
                                    : progress < 66
                                    ? 'bg-gradient-to-r from-yellow-400 to-amber-600'
                                    : 'bg-gradient-to-r from-red-400 to-rose-600'
                                }`}
                                style={{ width: `${progress}%` }}
                              />
                            </div>
                            <div className="mt-3 text-[11px] flex flex-wrap gap-4 text-gray-600">
                              <span className="font-semibold"><span className="text-blue-700 font-bold">Batch Start:</span> {formatDate(batch.batchStartingDate)}</span>
                              <span className="font-semibold"><span className="text-purple-700 font-bold">Batch End:</span> {formatDate(batch.batchEndingDate)}</span>
                              <span className="font-semibold"><span className="text-indigo-700 font-bold">Total Days:</span> {totalDays}</span>
                              <span className="font-semibold"><span className="text-green-700 font-bold">Passed Days:</span> {passedDays}</span>
                              <span className="font-semibold"><span className="text-amber-700 font-bold">Pending Days:</span> {pendingDays}</span>
                            </div>
                          </td>
                        </tr>
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <DashboardLayout>
      <div className="py-6">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Course Delivery</h1>
                <p className="mt-1 text-sm text-gray-600">Select a course type to view and manage delivery information</p>
              </div>
              <div className="flex gap-3 items-center">
                {isAdminUser && (
                  <button
                    onClick={handleOpenNotifications}
                    className="rounded-lg bg-gradient-to-r from-orange-500 via-amber-500 to-yellow-500 px-4 py-3 text-sm font-semibold text-white shadow-md transition-all hover:shadow-lg"
                  >
                    Get Notifications
                  </button>
                )}
                <div className="rounded-lg bg-gradient-to-br from-indigo-50 to-purple-50 border-2 border-indigo-200 px-4 py-3 shadow-md">
                  <p className="text-xs font-semibold text-indigo-600 uppercase tracking-wide">Total Batches</p>
                  <p className="mt-1 text-2xl font-bold text-indigo-900">{batches.length}</p>
                </div>
                {searchTerm && (
                  <div className="rounded-lg bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200 px-4 py-3 shadow-md">
                    <p className="text-xs font-semibold text-green-600 uppercase tracking-wide">Search Results</p>
                    <p className="mt-1 text-2xl font-bold text-green-900">{filteredBatches.length}</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="mb-6">
            <div className="border-b border-gray-200">
              <nav className="-mb-px flex space-x-4 overflow-x-auto" aria-label="Tabs">
                {visibleTabs.map((tab) => (
                  <button
                    key={tab.key}
                    onClick={() => handleCourseTabChange(tab.key)}
                    className={`
                      whitespace-nowrap border-b-2 px-4 py-3 text-sm font-medium transition-colors duration-200
                      ${
                        activeTab === tab.key
                          ? 'border-indigo-600 text-indigo-600'
                          : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                      }
                    `}
                    aria-current={activeTab === tab.key ? 'page' : undefined}
                  >
                    {tab.label}
                  </button>
                ))}
              </nav>
            </div>
          </div>

          <div className="mb-6">
            <div className="bg-gray-50 rounded-lg p-2">
              <div className="flex space-x-2 overflow-x-auto">
                {statusTabs.map((statusTab) => (
                  <button
                    key={statusTab.key}
                    onClick={() => handleStatusTabChange(statusTab.key)}
                    className={`
                      whitespace-nowrap rounded-md px-4 py-2 text-sm font-medium transition-all duration-200
                      ${
                        activeStatusTab === statusTab.key
                          ? statusTab.color === 'gray'
                            ? 'bg-gray-700 text-white shadow-sm'
                            : statusTab.color === 'blue'
                            ? 'bg-blue-600 text-white shadow-sm'
                            : statusTab.color === 'yellow'
                            ? 'bg-yellow-500 text-white shadow-sm'
                            : statusTab.color === 'green'
                            ? 'bg-green-600 text-white shadow-sm'
                            : 'bg-purple-600 text-white shadow-sm'
                          : 'bg-white text-gray-700 hover:bg-gray-100'
                      }
                    `}
                  >
                    {statusTab.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="mb-6">
            <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-gray-800">Color legend</h3>
                <span className="text-[11px] text-gray-500">Hover items for details</span>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                <div className="flex items-center gap-3" title="Actual assignment dates are recorded for this subject">
                  <div className="h-5 w-5 rounded-md ring-2 ring-green-500 bg-gradient-to-br from-green-200 via-emerald-200 to-green-100" aria-label="Released highlight"></div>
                  <div>
                    <div className="text-xs font-semibold text-gray-800">Released</div>
                    <div className="text-[11px] text-gray-600">Actual assignment dates recorded</div>
                  </div>
                </div>
                <div className="flex items-center gap-3" title="Subject is marked completed but the Actual Submission Date has not been set">
                  <div className="h-5 w-5 rounded-md ring-2 ring-amber-400 bg-gradient-to-br from-amber-100 via-yellow-100 to-amber-50" aria-label="Unreleased highlight"></div>
                  <div>
                    <div className="text-xs font-semibold text-gray-800">Unreleased</div>
                    <div className="text-[11px] text-gray-600">Completed, missing actual submission date</div>
                  </div>
                </div>
                <div className="flex items-center gap-3" title="Scheduled hours exceed expected hours (E) before completion">
                  <div className="h-5 w-5 rounded-md ring-2 ring-fuchsia-400 bg-gradient-to-br from-fuchsia-100 via-pink-100 to-rose-100" aria-label="Over scheduled highlight"></div>
                  <div>
                    <div className="text-xs font-semibold text-gray-800">Over scheduled</div>
                    <div className="text-[11px] text-gray-600">Scheduled hours exceed expected</div>
                  </div>
                </div>
                <div className="flex items-center gap-3" title="End date passed and subject not completed yet">
                  <div className="h-5 w-5 rounded-md ring-2 ring-red-400 bg-gradient-to-br from-red-50 to-red-100" aria-label="Overdue highlight"></div>
                  <div>
                    <div className="text-xs font-semibold text-gray-800">Overdue</div>
                    <div className="text-[11px] text-gray-600">End date passed, not completed</div>
                  </div>
                </div>
                <div className="flex items-center gap-3" title="Subject completed and end date passed">
                  <div className="h-5 w-5 rounded-md ring-1 ring-gray-300 bg-gradient-to-br from-gray-50 to-gray-100" aria-label="Completed and ended highlight"></div>
                  <div>
                    <div className="text-xs font-semibold text-gray-800">Completed and Ended</div>
                    <div className="text-[11px] text-gray-600">Completed and past end date</div>
                  </div>
                </div>
                <div className="flex items-center gap-3" title="Status badge colors">
                  <div className="flex flex-wrap gap-1" aria-label="Status badges legend">
                    <span className="inline-block rounded-full px-2 py-0.5 text-[10px] font-bold bg-gray-100 text-gray-800">not started</span>
                    <span className="inline-block rounded-full px-2 py-0.5 text-[10px] font-bold bg-blue-100 text-blue-800">ongoing</span>
                    <span className="inline-block rounded-full px-2 py-0.5 text-[10px] font-bold bg-green-100 text-green-800">completed</span>
                    <span className="inline-block rounded-full px-2 py-0.5 text-[10px] font-bold bg-purple-100 text-purple-800">delivery completed</span>
                    <span className="inline-block rounded-full px-2 py-0.5 text-[10px] font-bold bg-green-100 text-green-800">batch completed</span>
                  </div>
                  <div>
                    <div className="text-xs font-semibold text-gray-800">Status badges</div>
                    <div className="text-[11px] text-gray-600">Consistent colors for subject and batch status</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mb-6">
            <div className="relative">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4">
                <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by batch name, WhatsApp group name, or internal name..."
                className="block w-full rounded-xl border-2 border-gray-300 bg-white py-3 pl-11 pr-12 text-sm text-gray-900 placeholder-gray-400 transition-all duration-200 focus:border-indigo-500 focus:outline-none focus:ring-4 focus:ring-indigo-500/20 hover:border-gray-400"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
            {searchTerm && (
              <p className="mt-2 text-sm text-gray-600">
                Showing <span className="font-semibold text-indigo-600">{filteredBatches.length}</span> of <span className="font-semibold">{batches.length}</span> batches
              </p>
            )}
          </div>

          <div className="mt-6">
            {loading ? (
              <div className="rounded-lg border border-gray-200 bg-white p-6 text-center text-gray-600">Loading delivery data...</div>
            ) : error ? (
              <div className="rounded-lg border border-red-200 bg-red-50 p-6 text-center text-red-700">{error}</div>
            ) : (
              renderStatusContent()
            )}
          </div>
        </div>
      </div>

      {isAdminUser && showNotificationModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4 overflow-y-auto">
          <div className="relative w-full max-w-6xl my-8 rounded-2xl bg-white shadow-2xl max-h-[calc(100vh-4rem)] flex flex-col">
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 flex-shrink-0">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Delivery Notifications</h2>
                <p className="text-sm text-gray-600">Showing {activeCourseLabel} course subjects</p>
              </div>
              <button
                onClick={() => setShowNotificationModal(false)}
                className="rounded-full bg-gray-100 p-2 text-gray-600 transition-all hover:bg-gray-200 hover:text-gray-900"
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="border-b border-gray-100 px-6 py-3 flex-shrink-0">
              <div className="flex flex-wrap gap-2">
                {notificationTabs.map((tab) => {
                  const count = notificationBuckets[tab.key]?.length || 0;
                  return (
                    <button
                      key={tab.key}
                      onClick={() => setActiveNotificationTab(tab.key)}
                      className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold transition-all ${
                        activeNotificationTab === tab.key
                          ? 'bg-indigo-600 text-white shadow-md'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      } ${count === 0 ? 'opacity-60' : ''}`}
                    >
                      <span>{tab.label}</span>
                      <span
                        className={`${
                          activeNotificationTab === tab.key
                            ? 'bg-white/20 text-white'
                            : 'bg-white text-gray-700'
                        } rounded-full px-2 py-1 text-xs font-bold`}
                      >
                        {count}
                      </span>
                    </button>
                  );
                })}
              </div>
              <div className="mt-3 space-y-3">
                <div className="flex items-center gap-2">
                  <div className="relative flex-1">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400">
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                    <input
                      value={notificationSearchTerm}
                      onChange={(e) => setNotificationSearchTerm(e.target.value)}
                      placeholder="Search by course, batch, subject, lecturer, status... (use commas for multiple)"
                      className="w-full rounded-lg border border-gray-300 py-2 pl-9 pr-10 text-sm text-gray-800 placeholder-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                    />
                    {notificationSearchTerm && (
                      <button
                        onClick={() => setNotificationSearchTerm('')}
                        className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
                      >
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                  </div>
                  <div className="flex items-center gap-2 whitespace-nowrap text-xs font-semibold text-gray-600">
                    <span>Showing {filteredNotificationList.length} / {currentNotificationList.length}</span>
                    <span className="inline-flex items-center gap-1 rounded-full bg-indigo-50 px-2 py-1 text-indigo-700 border border-indigo-100">
                      Selected {selectedNotificationIds.length}
                    </span>
                    <button
                      onClick={handleCopySelectedNotifications}
                      className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={selectedNotificationIds.length === 0}
                    >
                      Copy Selected
                    </button>
                    <button
                      onClick={handleDownloadNotificationsExcel}
                      className="rounded-lg bg-teal-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={selectedNotificationIds.length === 0 || selectedNotificationExportColumns.length === 0}
                    >
                      Download Excel
                    </button>
                    <button
                      onClick={handleDownloadNotificationsPdf}
                      className="rounded-lg bg-rose-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:bg-rose-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={selectedNotificationIds.length === 0 || selectedNotificationExportColumns.length === 0}
                    >
                      Download PDF
                    </button>
                  </div>
                </div>
                
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                  <div className="mb-2 flex items-center justify-between">
                    <p className="text-xs font-bold text-gray-700">Export columns</p>
                    <div className="flex gap-2 text-[10px]">
                      <button
                        onClick={() => setSelectedNotificationExportColumns(notificationExportColumns.map((c) => c.id))}
                        className="rounded border border-gray-300 bg-white px-2 py-0.5 font-semibold text-gray-600 hover:border-indigo-400 hover:text-indigo-700"
                      >
                        Select All
                      </button>
                      <button
                        onClick={() => setSelectedNotificationExportColumns([])}
                        className="rounded border border-gray-300 bg-white px-2 py-0.5 font-semibold text-gray-600 hover:border-rose-400 hover:text-rose-700"
                      >
                        Clear All
                      </button>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {notificationExportColumns.map((col) => (
                      <label
                        key={col.id}
                        className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-xs cursor-pointer hover:border-indigo-300 hover:bg-indigo-50 transition-colors"
                      >
                        <input
                          type="checkbox"
                          className="h-3.5 w-3.5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                          checked={selectedNotificationExportColumns.includes(col.id)}
                          onChange={() => toggleNotificationExportColumn(col.id)}
                        />
                        <span className="font-medium text-gray-700">{col.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="p-6 overflow-y-auto flex-1">
              {filteredNotificationList.length === 0 ? (
                <div className="rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 p-8 text-center text-gray-600">
                  No items under {notificationTabs.find((t) => t.key === activeNotificationTab)?.label || 'this tab'}
                  {' '}for {activeCourseLabel} right now.
                </div>
              ) : (
                <div className="overflow-auto rounded-lg border border-gray-200 shadow-inner">
                  <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-50 sticky top-0 z-10">
                      <tr>
                        <th className="px-3 py-3 text-left font-semibold text-gray-700">
                          <input
                            type="checkbox"
                            aria-label="Select all"
                            checked={filteredNotificationList.length > 0 && filteredNotificationList.every((item) => selectedNotificationIds.includes(buildNotificationKey(item)))}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedNotificationIds(filteredNotificationList.map((item) => buildNotificationKey(item)));
                              } else {
                                setSelectedNotificationIds([]);
                              }
                            }}
                            className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                          />
                        </th>
                        <th className="px-3 py-3 text-left font-semibold text-gray-700">Course</th>
                        <th className="px-3 py-3 text-left font-semibold text-gray-700">Batch</th>
                        <th className="px-3 py-3 text-left font-semibold text-gray-700">Internal (Report No)</th>
                        <th className="px-3 py-3 text-left font-semibold text-gray-700">WhatsApp Group</th>
                        <th className="px-3 py-3 text-left font-semibold text-gray-700">Subject</th>
                        <th className="px-3 py-3 text-left font-semibold text-gray-700">Expected</th>
                        <th className="px-3 py-3 text-left font-semibold text-gray-700">Scheduled</th>
                        <th className="px-3 py-3 text-left font-semibold text-gray-700">Pending</th>
                        <th className="px-3 py-3 text-left font-semibold text-gray-700">Lecturer</th>
                        <th className="px-3 py-3 text-left font-semibold text-gray-700">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {Object.entries(groupedNotifications).map(([groupName, items]) => {
                        const [wgName, subjName] = groupName.split('||');
                        return (
                        <React.Fragment key={groupName}>
                          <tr className="bg-indigo-50">
                            <td colSpan={11} className="px-3 py-2 text-left text-sm font-bold text-indigo-800">
                              <span>{wgName}</span>
                              <span className="ml-2 text-xs font-semibold text-indigo-600">| {subjName}</span>
                              <span className="ml-2 rounded-full bg-white px-2 py-0.5 text-xs font-semibold text-indigo-700 border border-indigo-200">
                                {items.length} item{items.length !== 1 ? 's' : ''}
                              </span>
                            </td>
                          </tr>
                          {items.map((item) => {
                            const rowKey = buildNotificationKey(item);
                            const checked = selectedNotificationIds.includes(rowKey);
                            return (
                              <tr key={rowKey} className="hover:bg-indigo-50/60">
                                <td className="px-3 py-3">
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() => handleToggleNotificationSelect(item)}
                                    className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                  />
                                </td>
                                <td className="px-3 py-3 font-semibold text-gray-800">{item.courseShortName || 'N/A'}</td>
                                <td className="px-3 py-3 font-semibold text-gray-900">{item.batchName || 'N/A'}</td>
                                <td className="px-3 py-3 text-gray-700">{item.batchInternalName || 'N/A'}</td>
                                <td className="px-3 py-3 text-gray-700">{item.whatsappGroupName || 'N/A'}</td>
                                <td className="px-3 py-3 text-gray-800">
                                  <div className="font-semibold">{item.subjectName || 'N/A'}</div>
                                  <div className="text-xs text-gray-500">{item.subjectCode || ''}</div>
                                </td>
                                <td className="px-3 py-3 text-gray-800">
                                  {item.expectedHours != null
                                    ? `${item.expectedHours}h (${item.expectedClasses ?? '-'} classes)`
                                    : 'N/A'}
                                </td>
                                <td className="px-3 py-3 text-gray-800">
                                  {item.scheduledHours != null
                                    ? `${item.scheduledHours}h (${item.scheduledClasses ?? '-'} classes)`
                                    : 'N/A'}
                                  {item.expectedHours != null && item.scheduledHours != null && item.scheduledHours > item.expectedHours && (
                                    <div className="mt-1 inline-flex items-center rounded-md border border-rose-200 bg-rose-50 px-2 py-0.5 text-[11px] font-semibold text-rose-700">
                                      +{(item.scheduledHours - item.expectedHours).toFixed(1)}h
                                      {item.scheduledClasses != null && item.expectedClasses != null
                                        ? ` (${(item.scheduledClasses - item.expectedClasses).toFixed(1)} classes)`
                                        : ''}
                                    </div>
                                  )}
                                </td>
                                <td className="px-3 py-3 text-gray-800">
                                  {item.pendingHours != null
                                    ? `${item.pendingHours}h (${item.pendingClasses ?? '-'} classes)`
                                    : 'N/A'}
                                  {activeNotificationTab === 'overdue' && computeOverdueDays(item.endDate) > 0 && (
                                    <div className="mt-1 inline-flex items-center rounded-md border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700">
                                      Overdue by {computeOverdueDays(item.endDate)} day{computeOverdueDays(item.endDate) === 1 ? '' : 's'}
                                    </div>
                                  )}
                                </td>
                                <td className="px-3 py-3 text-gray-800">{item.lecturerName}</td>
                                <td className="px-3 py-3">
                                  <span className={`inline-block rounded-full px-3 py-1 text-xs font-bold ${getStatusColor(item.status)}`}>
                                    {item.status}
                                  </span>
                                </td>
                              </tr>
                            );
                          })}
                        </React.Fragment>
                      );
                      })}
                    </tbody>
                  </table>
                  <div className="mt-3 text-xs text-gray-500 px-2 pb-2">Showing {filteredNotificationList.length} item(s)</div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showSubjectModal && currentSubject && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-3">
          <div className="relative flex max-h-[90vh] w-full max-w-xl flex-col rounded-2xl bg-white shadow-2xl">
            <div className="flex-shrink-0 rounded-t-2xl bg-gradient-to-r from-indigo-600 to-purple-600 p-4">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-white">Edit Batch Subject</h2>
                <button
                  onClick={() => setShowSubjectModal(false)}
                  className="rounded-full bg-white bg-opacity-20 p-2 text-white transition-all hover:bg-opacity-30 hover:rotate-90"
                >
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              <div className="mb-3 rounded-lg border-l-4 border-indigo-500 bg-indigo-50 p-3">
                <div className="flex items-start">
                  <svg className="h-4 w-4 text-indigo-500 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <p className="text-sm font-semibold text-indigo-800">Batch group update</p>
                    <p className="mt-1 text-xs text-indigo-700">Applies to matching batch groups.</p>
                  </div>
                </div>
              </div>

              {currentSubject.AssignmentSubmissionDate && (() => {
                const daysDiff = computeDaysDiffFromToday(currentSubject.AssignmentSubmissionDate);
                const isPast = daysDiff < 0;
                const isToday = daysDiff === 0;
                const tone = isPast
                  ? { border: 'border-red-500', bg: 'bg-red-50', label: 'text-red-800', value: 'text-red-900', pill: 'bg-red-100 text-red-700' }
                  : isToday
                  ? { border: 'border-amber-500', bg: 'bg-amber-50', label: 'text-amber-800', value: 'text-amber-900', pill: 'bg-amber-100 text-amber-700' }
                  : { border: 'border-blue-500', bg: 'bg-blue-50', label: 'text-blue-800', value: 'text-blue-900', pill: 'bg-blue-100 text-blue-700' };
                return (
                  <div className={`mb-4 rounded-lg border-l-4 ${tone.border} ${tone.bg} p-3`}>
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className={`text-xs font-semibold ${tone.label}`}>Expected Assignment Submission Date</p>
                        <p className={`mt-0.5 text-sm font-bold ${tone.value}`}>{formatDate(currentSubject.AssignmentSubmissionDate)}</p>
                      </div>
                      <span className={`whitespace-nowrap rounded-full px-3 py-1 text-xs font-bold ${tone.pill}`}>
                        {isToday
                          ? 'Due today'
                          : isPast
                          ? `${Math.abs(daysDiff)} day${Math.abs(daysDiff) === 1 ? '' : 's'} passed`
                          : `${daysDiff} day${daysDiff === 1 ? '' : 's'} remaining`}
                      </span>
                    </div>
                  </div>
                );
              })()}

              <div className="mb-4 flex gap-2 rounded-lg bg-gray-100 p-2">
                <button
                  onClick={() => setUpdateType('primary')}
                  disabled={currentSubject.status !== 'completed'}
                  className={`flex-1 rounded-md px-4 py-2 text-sm font-semibold transition-all ${
                    updateType === 'primary'
                      ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-md'
                      : 'text-gray-600 hover:bg-gray-200'
                  } disabled:cursor-not-allowed disabled:opacity-50`}
                >
                  Primary Update
                </button>
                <button
                  onClick={() => setUpdateType('secondary')}
                  disabled={currentSubject.status !== 'completed'}
                  className={`flex-1 rounded-md px-4 py-2 text-sm font-semibold transition-all ${
                    updateType === 'secondary'
                      ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-md'
                      : 'text-gray-600 hover:bg-gray-200'
                  } disabled:cursor-not-allowed disabled:opacity-50`}
                >
                  Secondary Update
                </button>
              </div>

              {updateType === 'primary' && (
                <div className="space-y-3">
                  <div>
                    <label className="mb-2 block text-sm font-semibold text-gray-700">Subject Name</label>
                    <input
                      type="text"
                      value={currentSubject.subjectName}
                      readOnly
                      className="w-full rounded-lg border-2 border-gray-200 bg-gray-50 px-3 py-2 text-gray-700"
                    />
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-semibold text-gray-700">Status</label>
                    <select
                      value={currentSubject.status}
                      onChange={(e) => setCurrentSubject({ ...currentSubject, status: e.target.value })}
                      className="w-full rounded-lg border-2 border-gray-300 px-3 py-2 text-sm"
                    >
                      <option value="not started">Not Started</option>
                      <option value="ongoing">Ongoing</option>
                      <option value="completed">Completed</option>
                    </select>
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-semibold text-gray-700">Assign Lecturer</label>
                    <Select
                      value={selectedStaff}
                      onChange={(option) => setSelectedStaff(option)}
                      options={staff}
                      placeholder="Select a lecturer..."
                      isClearable
                      className="react-select-container"
                      classNamePrefix="react-select"
                      styles={{
                        control: (base, state) => ({
                          ...base,
                          borderColor: state.isFocused ? '#667eea' : '#d1d5db',
                          borderWidth: '2px',
                          borderRadius: '0.5rem',
                          padding: '0.25rem',
                          boxShadow: state.isFocused ? '0 0 0 3px rgba(102, 126, 234, 0.1)' : 'none',
                          '&:hover': { borderColor: '#667eea' },
                        }),
                        option: (base, state) => ({
                          ...base,
                          backgroundColor: state.isSelected ? '#667eea' : state.isFocused ? '#eef2ff' : 'white',
                          color: state.isSelected ? 'white' : '#374151',
                          cursor: 'pointer',
                          '&:active': { backgroundColor: '#667eea' },
                        }),
                        menu: (base) => ({
                          ...base,
                          borderRadius: '0.5rem',
                          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                          border: '1px solid #e5e7eb',
                        }),
                      }}
                      formatOptionLabel={(option) => (
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="font-semibold text-gray-900">{option.name}</div>
                            <div className="text-xs text-gray-500">{option.employeeNumber}</div>
                          </div>
                          {option.position && (
                            <span className="text-xs rounded-full bg-indigo-100 text-indigo-700 px-2 py-1">{option.position}</span>
                          )}
                        </div>
                      )}
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      {selectedStaff
                        ? `Selected: ${selectedStaff.name} - ${selectedStaff.position || 'No position'}`
                        : 'Choose a lecturer to assign to this subject'}
                    </p>
                  </div>
                </div>
              )}

              {updateType === 'secondary' && (
                <div className="space-y-3">
                  <div className="rounded-lg border-l-4 border-green-500 bg-green-50 p-3 mb-3">
                    <p className="text-xs font-semibold text-green-800">Actual assignment date management</p>
                    <p className="text-xs text-green-700 mt-1">Update when assignments were actually released and submitted.</p>
                  </div>

                  <div className="rounded-lg border-2 border-green-200 bg-green-50 p-3">
                    <h4 className="mb-2 text-sm font-bold text-green-900">Actual Assignment Dates</h4>
                    <div className="space-y-2">
                      <div>
                        <label className="mb-1 block text-xs font-semibold text-gray-700">Actual Release Date</label>
                        <input
                          type="date"
                          value={currentSubject.actualAssignmentReleaseDate || ''}
                          onChange={(e) =>
                            setCurrentSubject({ ...currentSubject, actualAssignmentReleaseDate: e.target.value })
                          }
                          className="w-full rounded-lg border-2 border-gray-300 px-3 py-2 text-sm transition-all focus:border-green-600 focus:ring-2 focus:ring-green-200"
                        />
                      </div>
                      <div>
                        <label className="mb-1 block text-xs font-semibold text-gray-700">Actual Submission Date</label>
                        <input
                          type="date"
                          value={currentSubject.actualAssignmentSubmissionDate || ''}
                          onChange={(e) => {
                            const submissionDate = e.target.value;
                            const { releaseDate, lateDate } = calculateAssignmentDates(submissionDate);
                            setCurrentSubject({
                              ...currentSubject,
                              actualAssignmentSubmissionDate: submissionDate,
                              actualAssignmentReleaseDate: releaseDate,
                              actualAssignmentLateSubmissionDate: lateDate,
                            });
                          }}
                          className="w-full rounded-lg border-2 border-gray-300 px-3 py-2 text-sm transition-all focus:border-green-600 focus:ring-2 focus:ring-green-200"
                        />
                        {currentSubject.actualAssignmentSubmissionDate && (() => {
                          const vsExpected = currentSubject.AssignmentSubmissionDate
                            ? daysBetweenDates(currentSubject.AssignmentSubmissionDate, currentSubject.actualAssignmentSubmissionDate)
                            : null;
                          const vsToday = computeDaysDiffFromToday(currentSubject.actualAssignmentSubmissionDate);
                          return (
                            <div className="mt-1.5 flex flex-wrap gap-1.5">
                              {vsExpected !== null && (
                                <span
                                  className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                                    vsExpected > 0
                                      ? 'bg-red-100 text-red-700'
                                      : vsExpected < 0
                                      ? 'bg-green-100 text-green-700'
                                      : 'bg-gray-100 text-gray-700'
                                  }`}
                                >
                                  {vsExpected > 0
                                    ? `${vsExpected} day${vsExpected === 1 ? '' : 's'} late vs expected date`
                                    : vsExpected < 0
                                    ? `${Math.abs(vsExpected)} day${Math.abs(vsExpected) === 1 ? '' : 's'} early vs expected date`
                                    : 'On time vs expected date'}
                                </span>
                              )}
                              {vsToday !== null && (
                                <span
                                  className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                                    vsToday > 0
                                      ? 'bg-blue-100 text-blue-700'
                                      : vsToday < 0
                                      ? 'bg-gray-200 text-gray-700'
                                      : 'bg-amber-100 text-amber-700'
                                  }`}
                                >
                                  {vsToday > 0
                                    ? `${vsToday} day${vsToday === 1 ? '' : 's'} remaining from today`
                                    : vsToday < 0
                                    ? `${Math.abs(vsToday)} day${Math.abs(vsToday) === 1 ? '' : 's'} passed from today`
                                    : 'Actual date is today'}
                                </span>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                      <div>
                        <label className="mb-1 block text-xs font-semibold text-gray-700">Actual Late Submission Date</label>
                        <input
                          type="date"
                          value={currentSubject.actualAssignmentLateSubmissionDate || ''}
                          onChange={(e) =>
                            setCurrentSubject({ ...currentSubject, actualAssignmentLateSubmissionDate: e.target.value })
                          }
                          className="w-full rounded-lg border-2 border-gray-300 px-3 py-2 text-sm transition-all focus:border-green-600 focus:ring-2 focus:ring-green-200"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="flex-shrink-0 rounded-b-2xl bg-gray-50 px-6 py-4">
              <div className="flex justify-between gap-3">
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowSubjectModal(false)}
                    className="rounded-lg bg-gray-200 px-6 py-2 font-semibold text-gray-700 transition-all hover:bg-gray-300"
                  >
                    Close
                  </button>
                  {updateType === 'secondary' && hasActualAssignmentDates(currentSubject) && (
                    <button
                      onClick={handleClearActualDates}
                      className="rounded-lg bg-gradient-to-r from-red-500 to-red-600 px-6 py-2 font-semibold text-white shadow-lg transition-all hover:shadow-xl hover:from-red-600 hover:to-red-700"
                    >
                      Clear Dates
                    </button>
                  )}
                </div>
                <div className="flex gap-3">
                  {updateType === 'primary' && (
                    <button
                      onClick={handlePrimaryUpdate}
                      className="rounded-lg bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-2 font-semibold text-white shadow-lg transition-all hover:shadow-xl"
                    >
                      Save Changes
                    </button>
                  )}
                  {updateType === 'secondary' && (
                    <button
                      onClick={handleSecondaryUpdate}
                      className="rounded-lg bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-2 font-semibold text-white shadow-lg transition-all hover:shadow-xl"
                    >
                      Update Dates
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {showBatchModal && currentBatch && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="relative w-full max-w-lg rounded-2xl bg-white shadow-2xl">
            <div className="rounded-t-2xl bg-gradient-to-r from-indigo-600 to-purple-600 p-6">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-white">Change Batch Status</h2>
                <button
                  onClick={() => setShowBatchModal(false)}
                  className="rounded-full bg-white bg-opacity-20 p-2 text-white transition-all hover:bg-opacity-30 hover:rotate-90"
                >
                  <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="mb-2 block text-sm font-semibold text-gray-700">Batch Name</label>
                  <input
                    type="text"
                    value={currentBatch.batchName}
                    readOnly
                    className="w-full rounded-lg border-2 border-gray-200 bg-gray-50 px-4 py-2 text-gray-600"
                  />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-semibold text-gray-700">Status</label>
                  <select
                    value={currentBatch.status}
                    onChange={(e) => setCurrentBatch({ ...currentBatch, status: e.target.value })}
                    className="w-full rounded-lg border-2 border-gray-300 px-4 py-2 transition-all"
                  >
                    <option value="not started">Not Started</option>
                    <option value="ongoing">Ongoing</option>
                    <option value="delivery completed">Delivery Completed</option>
                    <option value="batch completed">Batch Completed</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="rounded-b-2xl bg-gray-50 px-6 py-4">
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowBatchModal(false)}
                  className="rounded-lg bg-gray-200 px-6 py-2 font-semibold text-gray-700 transition-all hover:bg-gray-300"
                >
                  Close
                </button>
                <button
                  onClick={handleBatchStatusUpdate}
                  className="rounded-lg bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-2 font-semibold text-white shadow-lg transition-all hover:shadow-xl"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
